import { type CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { type Subject } from 'rxjs';

import { type ScrollOffsetAdapter } from '../scroll-offset-adapter/scroll-offset-adapter';

import { type VirtualScrollNodesProvider } from './item-index-provider';
import { type ItemSizePredictor } from './item-size-predictor';

interface ItemSize {
  value: number;
  source: 'predicted' | 'actual';
}

export class BlazeAutosizeStrategy<T> {
  private readonly sizeCache = new Map<number, ItemSize>();
  private wrapper: ChildNode | null = null;
  private itemsBufferCount: number;
  private items: T[] | null = null;

  constructor(
    private readonly itemSizePredictor: ItemSizePredictor<T>,
    private readonly scrolledIndexChange$$: Subject<number>,
    private readonly nodesProvider: VirtualScrollNodesProvider,
    private readonly scrollAdapter: ScrollOffsetAdapter,
    itemsBufferCount: number,
    items: T[] | null,
  ) {
    this.itemsBufferCount = itemsBufferCount;
    this.items = items;
  }

  attach(viewport: CdkVirtualScrollViewport): void {
    this.wrapper = viewport.getElementRef().nativeElement.childNodes[0];

    if (this.items) {
      viewport.setTotalContentSize(this.getTotalSize());
      this.updateRenderedRange(viewport);
    }
  }

  detach(): void {
    this.wrapper = null;
  }

  resetSizeCache(viewport: CdkVirtualScrollViewport | null): void {
    this.sizeCache.clear();

    if (viewport) {
      this.onDataLengthChanged(viewport);
    }
  }

  updateItemsBufferCount(value: number, viewport: CdkVirtualScrollViewport | null): void {
    this.itemsBufferCount = value;

    if (viewport) {
      this.updateRenderedRange(viewport);
    }
  }

  onDataLengthChanged(viewport: CdkVirtualScrollViewport): void {
    this.updateTotalContentSize(viewport);
    this.updateRenderedRange(viewport);
  }

  scrollToIndex(viewport: CdkVirtualScrollViewport, index: number, behavior: ScrollBehavior): void {
    const offset = this.getOffsetByItemIdx(index);
    viewport.scrollToOffset(offset, behavior);
  }

  updateRenderedRange(viewport: CdkVirtualScrollViewport): void {
    const scrollOffset = this.scrollAdapter.measureScrollOffset(viewport);
    const scrollIdx = this.getItemIdxByOffset(scrollOffset);
    const dataLength = viewport.getDataLength();
    const renderedRange = viewport.getRenderedRange();
    const range = {
      start: renderedRange.start,
      end: renderedRange.end,
    };

    range.start = Math.max(0, scrollIdx - this.itemsBufferCount);
    range.end = Math.min(
      dataLength,
      scrollIdx + this.determineItemsCountInViewport(viewport, scrollIdx) + this.itemsBufferCount,
    );

    viewport.setRenderedRange(range);
    viewport.setRenderedContentOffset(this.getOffsetByItemIdx(range.start));
    this.scrolledIndexChange$$.next(scrollIdx);
    this.updateSizeCache(viewport);
  }

  updateTotalContentSize(viewport: CdkVirtualScrollViewport): void {
    viewport.setTotalContentSize(this.getTotalSize());
  }

  updateItems(items: T[], viewport: CdkVirtualScrollViewport | null): void {
    this.items = items;

    if (viewport) {
      viewport.checkViewportSize();
    }
  }

  updateItemSize(viewport: CdkVirtualScrollViewport, index: number, value: number): void {
    const currentCache = this.sizeCache.get(index);
    if (currentCache?.source === 'actual' && currentCache.value === value) {
      return;
    }

    this.sizeCache.set(index, {
      value,
      source: 'actual',
    });

    viewport.setTotalContentSize(this.getTotalSize());
  }

  private updateSizeCache(viewport: CdkVirtualScrollViewport): void {
    if (!this.wrapper) {
      return;
    }

    const nodes = this.nodesProvider.getDOMNodes(this.wrapper);
    let cacheUpdated = false;

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i] as HTMLElement;
      const index = this.nodesProvider.getIndexOfElement(node);
      const cachedSize = this.sizeCache.get(index);

      if (!cachedSize || cachedSize.source !== 'actual') {
        const size = this.nodesProvider.getSize(node);
        this.sizeCache.set(index, { value: size, source: 'actual' });
        cacheUpdated = true;
      }
    }

    if (cacheUpdated) {
      viewport.setTotalContentSize(this.getTotalSize());
    }
  }

  private getTotalSize(): number {
    return this.measureItemsSize(this.items ?? []);
  }

  private getOffsetByItemIdx(idx: number): number {
    return this.measureItemsSize(this.items?.slice(0, idx) ?? []);
  }

  private getItemIdxByOffset(offset: number): number {
    let sum = 0;

    if (!this.items) {
      return 0;
    }

    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i];
      sum += this.getItemSize(item, i);

      if (sum >= offset) {
        return i;
      }
    }

    // Scroll offset exceeds total size (can happen when the size cache is
    // reset while the browser scroll position still reflects the previous larger total).
    // Return the last valid index so the viewport stays at the bottom instead of
    // jumping back to index 0.
    return Math.max(0, this.items.length - 1);
  }

  private getItemSize(item: T, index: number): number {
    let size = 0;
    const cachedSize = this.sizeCache.get(index);
    if (cachedSize) {
      size = cachedSize.value;
    } else {
      size = this.itemSizePredictor.getPredictedSize(item, index);
      this.sizeCache.set(index, {
        value: size,
        source: 'predicted',
      });
    }

    return size;
  }

  private measureItemsSize(items: any[]): number {
    return items.map((item, index) => this.getItemSize(item, index)).reduce((a, c) => a + c, 0);
  }

  private determineItemsCountInViewport(
    viewport: CdkVirtualScrollViewport,
    startIdx: number,
  ): number {
    if (!this.items) {
      return 0;
    }

    let totalSize = 0;
    const viewportSize = viewport.getViewportSize();

    for (let i = startIdx; i < this.items.length; i++) {
      const item = this.items[i];
      totalSize += this.getItemSize(item, i);

      if (totalSize >= viewportSize) {
        return i - startIdx + 1;
      }
    }

    return this.items.length;
  }
}
